import React, { FC, Fragment, useState } from 'react';
import { Button, ListItem, ListItemText, ListItemButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PropertyList } from 'components/property-settings/property-shared-styles';
import { ActionMaterial } from 'models/order-input/lab-order';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type ActionMaterialDialogProps = {
    onClose: () => void;
    open: boolean;
    onAddMaterial: (selectedMaterial?: ActionMaterial) => void;
    actionMaterials: ActionMaterial[];
    isMaterialRequired: boolean;
    // Names the product being asked about, for prompts shown detached from the grid row.
    actionLabel?: string;
};
const ActionMaterialDialog: FC<ActionMaterialDialogProps> = ({
    onClose,
    open,
    onAddMaterial,
    actionMaterials,
    isMaterialRequired,
    actionLabel,
}: ActionMaterialDialogProps) => {
    const { t: translate } = useTranslation(['actions-materials', 'common']);
    const [selectedMaterial, setSelectedMaterial] = useState<ActionMaterial>();

    const handleListItemClick = (material: ActionMaterial) => {
        setSelectedMaterial(material);
    };

    const handleClick = () => {
        onAddMaterial(selectedMaterial);
        setSelectedMaterial(undefined);
        onClose();
    };

    return (
        <Fragment>
            <DHDialog
                title={
                    <Fragment>
                        {translate('actions-materials:MATERIAL')}
                        {isMaterialRequired && (
                            <Typography ml={0.2} sx={{ color: 'red', display: 'inline' }}>
                                *
                            </Typography>
                        )}
                    </Fragment>
                }
                maxWidth="sm"
                fullWidth
                open={open}
                onClose={() => undefined}
                actions={
                    <Fragment>
                        {!isMaterialRequired && (
                            <Button onClick={onClose}>{translate('actions-materials:ADD_NO_MATERIAL')}</Button>
                        )}
                        <Button disabled={isMaterialRequired && !selectedMaterial?._id} onClick={handleClick}>
                            {translate('common:ADD')}
                        </Button>
                    </Fragment>
                }
            >
                {actionLabel ? (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {translate('actions-materials:FOR_PRODUCT', {
                            product: actionLabel,
                            // React escapes the text; i18next's escaping would show a "/" as &#x2F;.
                            interpolation: { escapeValue: false },
                        })}
                    </Typography>
                ) : undefined}
                <PropertyList>
                    {actionMaterials?.map((material) => {
                        return (
                            <ListItem key={material._id} disableGutters>
                                <ListItemButton
                                    onClick={() => handleListItemClick(material)}
                                    sx={{
                                        outline: selectedMaterial?._id === material._id ? '2px solid #999' : 'none',
                                    }}
                                    selected={selectedMaterial?._id === material._id}
                                >
                                    <ListItemText primary={material.name} />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </PropertyList>
            </DHDialog>
        </Fragment>
    );
};

export default ActionMaterialDialog;
